import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Briefcase, Heart, MessageCircle, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { mockMatches } from "@/data/mockMatches";
import { SwipeModal } from "@/components/swipe/SwipeModal";

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const [isSwipeModalOpen, setIsSwipeModalOpen] = useState(false);

  // Redirect to auth if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/auth");
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: "var(--color-bg-primary)" }}
      >
        <p style={{ color: "var(--color-text-secondary)" }}>Laster...</p>
      </div>
    );
  }

  // Show loading or nothing while redirecting
  if (!isAuthenticated) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: "var(--color-bg-primary)" }}
      >
        <p style={{ color: "var(--color-text-secondary)" }}>Laster...</p>
      </div>
    );
  }

  // Get data from mock matches
  const totalMatches = mockMatches.length;
  const unreadMessages = mockMatches.reduce((sum, m) => sum + m.unreadCount, 0);
  const availableJobs = 15;
  const userRole = user?.role || "seeker";

  // Determine what to show based on user role
  const isSeeker = userRole === "seeker";
  const isEmployer = userRole === "employer";

  return (
    <div style={{ backgroundColor: "var(--color-bg-primary)" }}>
      <main className="px-4" style={{ paddingTop: "var(--space-lg)" }}>
        <div className="mx-auto max-w-md">
          {/* Quick stats - only show if there's data */}
          {(totalMatches > 0 || unreadMessages > 0) && (
            <div className="mb-4 grid grid-cols-2 gap-3">
              {unreadMessages > 0 && (
                <QuickStat
                  icon={MessageCircle}
                  value={unreadMessages}
                  label="Uleste meldinger"
                  href="/chat"
                />
              )}
              {totalMatches > 0 && (
                <QuickStat icon={Heart} value={totalMatches} label="Matches" href="/matches" />
              )}
            </div>
          )}

          {/* Main action cards - Always show 4 cards */}
          <div className="mb-6 space-y-4">
            {/* Card 1: Swipe/Jobs */}
            {isSeeker && (
              <div
                onClick={() => setIsSwipeModalOpen(true)}
                className="block cursor-pointer rounded-[var(--radius-md)] p-4 backdrop-blur-[var(--blur-backdrop)] transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  backgroundColor: "rgba(38, 38, 43, 0.9)",
                  border: `1px solid rgba(255, 255, 255, 0.12)`,
                  boxShadow: "var(--shadow-floating)",
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)]"
                    style={{ background: "var(--color-bg-accent)" }}
                  >
                    <Briefcase className="h-5 w-5" style={{ color: "var(--color-text-primary)" }} />
                  </div>
                  <div className="flex-1">
                    <h3
                      className="font-semibold"
                      style={{
                        fontSize: "var(--font-size-body-lg)",
                        fontWeight: "var(--font-weight-semibold)",
                        color: "var(--color-text-primary)",
                      }}
                    >
                      {availableJobs > 0 ? `${availableJobs} nye jobber` : "Ingen nye jobber"}
                    </h3>
                    <p
                      style={{
                        fontSize: "var(--font-size-body-md)",
                        color: "var(--color-text-secondary)",
                      }}
                    >
                      Swipe for å finne din neste jobb
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isEmployer && (
              <ActionCard
                icon={Users}
                title="Swipe kandidater"
                description="Se kandidater som har likt dine stillinger"
                href="/candidates"
                primary
              />
            )}

            {/* Card 2: Matches - Always show */}
            <ActionCard
              icon={Heart}
              title={totalMatches > 0 ? `${totalMatches} matches` : "Matches"}
              description={totalMatches > 0 ? "Se dine matches" : "Du har ingen matches ennå"}
              href="/matches"
              badge={totalMatches > 0 ? "2 ny" : undefined}
            />

            {/* Card 3: Messages - Always show */}
            <ActionCard
              icon={MessageCircle}
              title={unreadMessages > 0 ? "Nye meldinger" : "Meldinger"}
              description={
                unreadMessages > 0 ? `${unreadMessages} uleste meldinger` : "Ingen nye meldinger"
              }
              href="/chat"
            />
          </div>

          {/* Primary CTA Button */}
          {isSeeker ? (
            <Button
              onClick={() => setIsSwipeModalOpen(true)}
              className="w-full rounded-[var(--radius-sm)] font-semibold transition-all duration-[var(--motion-duration-fast)]"
              style={{
                background: "var(--color-bg-accent)",
                padding: "var(--space-md) var(--space-lg)",
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                boxShadow: "var(--shadow-card)",
              }}
            >
              Start å swipe
            </Button>
          ) : (
            <Button
              asChild
              className="w-full rounded-[var(--radius-sm)] font-semibold transition-all duration-[var(--motion-duration-fast)]"
              style={{
                background: "var(--color-bg-accent)",
                padding: "var(--space-md) var(--space-lg)",
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                boxShadow: "var(--shadow-card)",
              }}
            >
              <Link to="/candidates">Se kandidater</Link>
            </Button>
          )}
        </div>
      </main>

      {/* Swipe Modal */}
      <SwipeModal isOpen={isSwipeModalOpen} onClose={() => setIsSwipeModalOpen(false)} />
    </div>
  );
}

function QuickStat({
  icon: Icon,
  value,
  label,
  href,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  value: number | string;
  label: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="block rounded-[var(--radius-md)] p-3 backdrop-blur-[var(--blur-backdrop)] transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
      style={{
        backgroundColor: "rgba(32, 32, 37, 0.85)",
        border: `1px solid rgba(255, 255, 255, 0.12)`,
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div
        className="mb-1.5 inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-sm)]"
        style={{ background: "var(--color-bg-accent)" }}
      >
        <Icon className="h-4 w-4" style={{ color: "var(--color-text-primary)" }} />
      </div>
      <p
        className="font-bold"
        style={{
          fontSize: "var(--font-size-body-lg)",
          fontWeight: "var(--font-weight-bold)",
          color: "var(--color-text-primary)",
        }}
      >
        {value}
      </p>
      <p style={{ fontSize: "var(--font-size-caption)", color: "var(--color-text-secondary)" }}>
        {label}
      </p>
    </Link>
  );
}

function ActionCard({
  icon: Icon,
  title,
  description,
  href,
  badge,
  primary = false,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string;
  description: string;
  href: string;
  badge?: string;
  primary?: boolean;
}) {
  return (
    <Link
      to={href}
      className="block rounded-[var(--radius-md)] p-4 backdrop-blur-[var(--blur-backdrop)] transition-all duration-[var(--motion-duration-medium)] hover:scale-[1.02] active:scale-[0.98]"
      style={{
        backgroundColor: primary ? "rgba(38, 38, 43, 0.9)" : "rgba(32, 32, 37, 0.85)",
        border: `1px solid rgba(255, 255, 255, 0.12)`,
        boxShadow: primary ? "var(--shadow-floating)" : "var(--shadow-card)",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-sm)]"
          style={{ background: "var(--color-bg-accent)" }}
        >
          <Icon className="h-5 w-5" style={{ color: "var(--color-text-primary)" }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3
              className="font-semibold"
              style={{
                fontSize: "var(--font-size-body-lg)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-primary)",
              }}
            >
              {title}
            </h3>
            {badge && (
              <span
                className="rounded-full px-2 py-0.5 text-xs font-semibold"
                style={{
                  background: "var(--color-bg-accent)",
                  color: "var(--color-text-primary)",
                  fontSize: "var(--font-size-caption)",
                }}
              >
                {badge}
              </span>
            )}
          </div>
          <p style={{ fontSize: "var(--font-size-body-md)", color: "var(--color-text-secondary)" }}>
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
}

